/**
 * Free power window overlap calculator.
 * Determines what fraction of a pricing tier overlaps with a free/discount window.
 */

export type HourRange = [number, number]

/**
 * Calculate overlap fraction between a free window and pricing tiers.
 * @param startH - free window start hour (0-23)
 * @param endH - free window end hour (0-23)
 * @param tierRanges - array of [start, end] hour ranges for the tier
 * @param tierTotalH - total hours in the tier
 * @returns fraction of tier hours that overlap (0-1)
 */
export const calcFreeOverlap = (
  startH: number,
  endH: number,
  tierRanges: HourRange[],
  tierTotalH: number
): number => {
  const freeRanges: HourRange[] =
    startH < endH
      ? [[startH, endH]]
      : startH > endH
        ? [
            [startH, 24],
            [0, endH],
          ]
        : []

  let overlap = 0
  for (const [fs, fe] of freeRanges) {
    for (const [ts, te] of tierRanges) {
      overlap += Math.max(0, Math.min(fe, te) - Math.max(fs, ts))
    }
  }
  return overlap / tierTotalH
}

/**
 * Calculate how many hours of a half-price window overlap with a tier.
 * Both ranges can wrap midnight.
 */
export const calcHalfPriceOverlapHours = (
  hStart: number,
  hEnd: number,
  tierStart: number,
  tierEnd: number
): number => {
  const toRanges = (s: number, e: number): HourRange[] =>
    s < e ? [[s, e]] : [
        [s, 24],
        [0, e],
      ]

  const halfRanges = toRanges(hStart, hEnd)
  const tierRanges = toRanges(tierStart, tierEnd)

  let overlap = 0
  for (const [hs, he] of halfRanges) {
    for (const [ts, te] of tierRanges) {
      overlap += Math.max(0, Math.min(he, te) - Math.max(hs, ts))
    }
  }
  return overlap
}

/**
 * Format an hour (0-23) as 12-hour string with am/pm.
 */
export const formatHour = (h: number): string =>
  `${h === 0 ? 12 : h > 12 ? h - 12 : h}${h < 12 ? 'am' : 'pm'}`

/**
 * Parse time string components into 24-hour format.
 */
export const parseTime = (n: string, ap: string): number => {
  let h = parseInt(n, 10)
  if (ap === 'pm' && h !== 12) h += 12
  if (ap === 'am' && h === 12) h = 0
  return h
}
